/**
 * Centralized environment variable access.
 *
 * All VITE_* env vars are read here and re-exported as typed properties.
 * Components and hooks import from this module instead of using
 * import.meta.env directly. When migrating to Next.js, only this file
 * changes (VITE_* -> NEXT_PUBLIC_* or process.env.*).
 *
 * NOTE: src/lib/envCheck.ts still uses import.meta.env directly for
 * startup validation -- that is intentional and expected.
 */

const SENTRY_ENVIRONMENTS = ["development", "staging", "production"] as const;

export type SentryEnvironment = (typeof SENTRY_ENVIRONMENTS)[number];

function resolveSentryEnvironment(): SentryEnvironment {
  const override = (
    import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined
  )
    ?.toLowerCase()
    ?.trim();
  if (
    override &&
    (SENTRY_ENVIRONMENTS as readonly string[]).includes(override)
  ) {
    return override as SentryEnvironment;
  }

  const mode = import.meta.env.MODE;
  if (mode === "staging") return "staging";
  if (mode === "production") return "production";
  if (mode === "development") return "development";
  return "development";
}

/**
 * apps/api origin for browser calls that already accept a Reklaim origin +
 * Clerk bearer (lifecycle survey submit/dismiss). Staging mode follows the
 * stable staging API host; every other mode, including production and tests,
 * uses the production API host unless VITE_API_URL is set.
 */
function resolveApiOrigin(): string {
  const configured = (
    (import.meta.env.VITE_API_URL as string | undefined) || ""
  )
    .trim()
    .replace(/\/+$/, "");
  if (configured) return configured;
  if (import.meta.env.MODE === "staging") return "https://api-next.reklaimyou.com";
  return "https://api.reklaimyou.com";
}

export const env = {
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL as string,
  supabaseKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
  analyticsEnv: import.meta.env.VITE_ANALYTICS_ENV as string,
  gtmId: import.meta.env.VITE_GTM_ID as string,
  gtmAuth: import.meta.env.VITE_GTM_AUTH as string,
  gtmPreview: import.meta.env.VITE_GTM_PREVIEW as string,
  gaMeasurementId: import.meta.env.VITE_GA_MEASUREMENT_ID as string,
  metaPixelId: import.meta.env.VITE_META_PIXEL_ID as string,
  posthogKey: import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string,
  posthogHost: import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string,
  appVersion: import.meta.env.VITE_APP_VERSION as string,
  brazeApiKey: import.meta.env.VITE_BRAZE_API_KEY as string,
  brazeSdkEndpoint: import.meta.env.VITE_BRAZE_SDK_ENDPOINT as string,
  sentryDsn: (import.meta.env.VITE_SENTRY_DSN as string | undefined) || "",
  sentryEnvironment: resolveSentryEnvironment(),
  // Absolute origin of the post-login app (where auth/transactional email
  // links must land). The marketing site is moving to the apex + www domains,
  // so email links MUST NOT use `window.location.origin` (which would be the
  // marketing apex) — they must target the app's own domain. Prod sets this to
  // https://web.reklaimyou.com; staging/dev leave it unset and fall back to the
  // current origin (see `getAppBaseUrl`). REK-1892.
  webAppUrl: ((import.meta.env.VITE_WEB_APP_URL as string | undefined) || "").trim(),
  // Absolute origin of the public marketing site (the apex). After the cutover
  // the marketing site owns the apex + www domains while this app lives at
  // web.reklaimyou.com, so "go home / log out" must leave the app for the
  // marketing root rather than navigate within the app. Prod sets this to
  // https://reklaimyou.com; staging to https://next.reklaimyou.com; dev leaves
  // it unset and falls back to the current origin (see getMarketingBaseUrl).
  // REK-1892.
  marketingUrl: ((import.meta.env.VITE_MARKETING_URL as string | undefined) || "").trim(),
  // Absolute origin of the Next.js app (redirect stub targets for surveys/campaigns).
  // Prod sets this to https://app.reklaimyou.com; staging to https://app-next.reklaimyou.com;
  // dev leaves it unset and falls back to the prod Next host (see getNextAppBaseUrl).
  // REK-2446.
  nextAppUrl: ((import.meta.env.VITE_NEXT_APP_URL as string | undefined) || "").trim(),
  // Vite survey entries hop to the Next app when this is absent or any value
  // other than off|false|0|no (trimmed, case-insensitive). The release ships
  // with the variable unset so the hop is live without a Doppler or Vercel
  // write. Do not trim here: "0" must survive (it means OFF).
  surveysNextHop:
    (import.meta.env.VITE_SURVEYS_NEXT_HOP as string | undefined) ?? "",
  // Vite pool entries hop to the Next app when this is absent or any value
  // other than off|false|0|no (trimmed, case-insensitive). The release ships
  // with the variable unset so the hop is live without a Doppler or Vercel
  // write. Do not trim here: "0" must survive (it means OFF).
  poolNextHop:
    (import.meta.env.VITE_POOL_NEXT_HOP as string | undefined) ?? "",
  // Absolute origin of apps/api (lifecycle survey submit/dismiss). Prod is
  // https://api.reklaimyou.com; staging builds (vite --mode staging) use
  // https://api-next.reklaimyou.com. VITE_API_URL overrides either host.
  // REK-2759. The Vite app calls these routes directly with a Clerk bearer;
  // it does not proxy through the Next app.
  apiOrigin: resolveApiOrigin(),
  // Clerk publishable key for the environment's Clerk instance
  // (staging: clerk.app-next.reklaimyou.com, production: clerk.app.reklaimyou.com).
  // Publishable keys are public by design (they ship in the browser bundle).
  clerkPublishableKey: (
    (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined) || ""
  ).trim(),
} as const;

